import { memo, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { buttonUIFactory, popupUIFactory, Widget } from '@yandex-int/messenger.widget';
import { useDeferred } from '../../lib/hooks';
import { MESSENGER_OPEN_EVENT, type MessengerOpenDetail } from '../../lib/messenger';
import { checkIsLiteStore } from '../../lib/storeInfo';
import { checkIsYCP } from '../../lib/ycp';
import { SupportChatContext } from './SupportChatContext';
import { useSupportChatMount } from './useSupportChatMount';

const KIT_SUPPORT_CHAT_CONFIG = {
    serviceId: 183,
    guid: '6c041b05-07a4-99cc-79be-8670b7aff93d',
} as const;

const MC_SUPPORT_CHAT_CONFIG = {
    serviceId: 148,
    guid: '7d33c602-4f85-9297-5e0b-310a3f7cb398',
} as const;

function getSupportChatConfig({ isLite, isYCP }: { isLite: boolean; isYCP: boolean }) {
    if (isLite) {
        return KIT_SUPPORT_CHAT_CONFIG;
    }

    if (isYCP) {
        return MC_SUPPORT_CHAT_CONFIG;
    }

    return KIT_SUPPORT_CHAT_CONFIG;
}

type MessengerUi = ReturnType<typeof buttonUIFactory> | ReturnType<typeof popupUIFactory>;

interface SupportChatProviderProps {
    children: ReactNode;
    /** Mount widget UI without the floating button. */
    headless?: boolean;
}

interface ChatState {
    widget: Widget | null;
    ui: MessengerUi | null;
    guid: string;
}

export const SupportChatProvider = memo(function SupportChatProvider({
    children,
    headless = false,
}: SupportChatProviderProps) {
    const isYCP = checkIsYCP();
    const isLite = checkIsLiteStore();
    const { serviceId, guid: chatGuid } = getSupportChatConfig({ isLite, isYCP });
    const [state, setState] = useState<ChatState>({
        widget: null,
        ui: null,
        guid: chatGuid,
    });
    const deferred = useDeferred();
    const pendingOpenRef = useRef<MessengerOpenDetail | null | undefined>(undefined);
    const widgetRef = useRef<Widget | null>(null);
    const guidRef = useRef(state.guid);

    useEffect(() => {
        widgetRef.current = state.widget;
        guidRef.current = state.guid;
    }, [state.widget, state.guid]);

    useEffect(() => {
        /** Откладываем инициализацию некритичного виджета до первого idle-периода */
        if (!deferred) {
            return undefined;
        }

        let cancelled = false;

        const guid = chatGuid;

        setState((prev) => {
            return {
                widget: null,
                ui: null,
                guid: prev.guid,
            };
        });

        Promise.all([
            import(/* webpackChunkName: "messenger-widget" */ '@yandex-int/messenger.widget'),
            headless
                ? import(/* webpackChunkName: "messenger-widget" */ '@yandex-int/messenger.widget/lib/ui/popup.css')
                : import(/* webpackChunkName: "messenger-widget" */ '@yandex-int/messenger.widget/lib/ui/button.css'),
        ]).then(([messenger]) => {
            if (cancelled) {
                return;
            }

            const unreadCounter = messenger.yandexUnreadCounterFactory();
            const ui = headless
                ? messenger.popupUIFactory({})
                : messenger.buttonUIFactory({
                      unreadCounterPlugin: unreadCounter,
                      buttonText: '',
                  });
            const widget = new messenger.Widget(
                new messenger.YandexConfig({
                    serviceId,
                    iframeOpenData: { guid },
                    flags: {
                        disableNavigation: '1',
                        visibilityMode: 'visible_and_focused',
                    },
                    unreadCounterOtherGuid: guid,
                    lang: 'ru',
                })
            );

            if (!headless) {
                widget.addPlugin(unreadCounter);
            }

            widget.setUI(ui).init();

            setState({ widget, ui, guid });
        });

        return () => {
            cancelled = true;
        };
    }, [chatGuid, deferred, headless, serviceId]);

    const { widget, ui, guid } = state;

    const { registerMountTarget, showMessenger } = useSupportChatMount({
        ui,
        headless,
        widgetRef,
        guidRef,
        pendingOpenRef,
    });

    useEffect(() => {
        const handleMessengerOpen = (event: Event) => {
            const detail = (event as CustomEvent<MessengerOpenDetail>).detail;

            showMessenger(detail);
        };

        window.addEventListener(MESSENGER_OPEN_EVENT, handleMessengerOpen);

        return () => {
            window.removeEventListener(MESSENGER_OPEN_EVENT, handleMessengerOpen);
        };
    }, [showMessenger]);

    const contextValue = useMemo(() => {
        return {
            widget,
            ui,
            guid,
            registerMountTarget,
        };
    }, [widget, ui, guid, registerMountTarget]);

    return <SupportChatContext.Provider value={contextValue}>{children}</SupportChatContext.Provider>;
});
